import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, PointElement, Title, Tooltip } from 'chart.js';
import createCustomLabelsPlugin from './plugins/createCustomLabelsPlugin';
import { moneyNumberFormatter } from './helpers';

ChartJS.register(CategoryScale, LinearScale, PointElement, BarElement, Title, Tooltip, Legend);

const ChartSaleHorizontalBar = ({
                                  label,
                                  labels,
                                  tooltipLabel = '',
                                  dataCounts,
                                  color,
                                  borderColor,
                                  addtitionalData={},
                                  additionalDatasets=[],
                                  currency,
                                  role  = false,
                                  useCustomLabels = true,
                                  topN = 6
}) => {
  const {t } = useTranslation();
  const hasCurrency = typeof currency === 'string' && currency.trim() !== '';

  // Normalization of labels - 2 representations
  const { labelObjects, labelStrings } = useMemo(() => {
    const objs = [];
    const strs = [];
    (Array.isArray(labels) ? labels : []).slice(0, topN).forEach(rawLabel => {
      let fullName = '';
      let roleText = '';
      let displayName = '';
      if (rawLabel && typeof rawLabel === 'object') {
        fullName = rawLabel.fullName || rawLabel.displayName || `${rawLabel.surname || ''} ${rawLabel.name || ''}`.trim();
        roleText = rawLabel.role || rawLabel.subtitle || '';
        displayName = rawLabel.displayName || fullName;
      } else {
        fullName = String(rawLabel || '');
        displayName = fullName;
      }
      objs.push({ fullName, role: roleText, displayName });
      const tickStr = role && roleText ? `${fullName}\n${roleText}` : fullName;
      strs.push(tickStr);
    });
    return { labelObjects: objs, labelStrings: strs };
  }, [labels, topN, role]);

  // Measuring the width of names - only if a plugin is needed
  const measuredMaxNameWidth = useMemo(() => {
    if (!useCustomLabels || typeof document === 'undefined' || !labelObjects.length) return 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const font = '400 16px Inter, Helvetica, sans-serif';
    ctx.font = font;
    let max = 0;
    labelObjects.forEach(l => {
      const w = ctx.measureText(l.fullName || '').width;
      if (w > max) max = w;
    });
    return Math.ceil(max);
  }, [useCustomLabels, labelObjects]);

  const leftPadding = useMemo(() => {
    if (!useCustomLabels) return 0;
    const extra = role ? 56 : 32;
    return Math.max(40, measuredMaxNameWidth + extra);
  }, [useCustomLabels, measuredMaxNameWidth, role]);

  const sellerLabel = useMemo(() => t('salesStatistics.Seller'), [t]);

  const customLabelsPlugin = useMemo(() => {
    if (!useCustomLabels) return null;
    return createCustomLabelsPlugin({
      leftPadding,
      role,
      sellerLabel,
      maxNameChars: 60
    });
  }, [useCustomLabels, leftPadding, role, sellerLabel]);



  const options = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    ...(useCustomLabels ? { layout: { padding: { left: leftPadding } } } : {}),
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const item = tooltipItems && tooltipItems[0];
            if (!item) return '';
            const rawLabel = item.chart?.data?.labels?.[item.dataIndex];
            if (rawLabel && typeof rawLabel === 'object') {
              return rawLabel.fullName || rawLabel.displayName || rawLabel.name || '';
            }
            return String(rawLabel ?? '');
          },
          label: (ctx) => {
            const raw = ctx.raw ?? 0;
            const n = Number(raw);
            if (hasCurrency) {
              const formatted = Number.isFinite(n) ? moneyNumberFormatter.format(n) : String(raw);
              return `${t(tooltipLabel)}: ${formatted} ${currency}`;
            }
            return `${t(tooltipLabel)}: ${n}`;
          }
        }
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          color: '#9D9D9D',
          font: { size: 14 },
          autoSkip: true,
          padding: 8,
          callback: (value) => {
            if (!hasCurrency) return value;
            const numberValue = Number(value);
            return Number.isFinite(numberValue) ? moneyNumberFormatter.format(numberValue) : value;
          }
        }
      },
      y: {
        ticks: {
          display: !useCustomLabels,
          color: '#1f3354',
          font: { size: 14 },
          padding: 8
        }
      }
    }
  };

  const data = {
    labels,
    datasets: [
      {
        data:  dataCounts.slice(0, topN),
        backgroundColor: color,
        borderColor: borderColor,
        borderWidth: 1,
        borderRadius: 4,
        ...addtitionalData
      },
      ...additionalDatasets
    ]
  };
  return (
    <div className="px-0" style={{ height: 330 }}>
      <Bar
        options={options}
        data={data}
        plugins={useCustomLabels && customLabelsPlugin ? [customLabelsPlugin] : []}
      />
    </div>
  );
};

export default ChartSaleHorizontalBar;